import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useClient } from '@utils/api-client';
import { CustomerReopenForReviewResponse } from '@lio/api-types';
import { customerQueryKey } from './keys';

/**
 * Reopen an active Confirmed customer for duplicate review (the inverse of
 * Confirm Distinct) when an Ops user identifies a possible identity conflict.
 * Requires a reason; suspected customers are optional context. Moving back to
 * NeedsReview re-applies the existing UW-referral / binding gate.
 */
export const useReopenForReview = () => {
  const client = useClient();
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: ({
      customerId,
      reason,
      suspectedCustomerIds,
    }: {
      customerId: string;
      reason: string;
      suspectedCustomerIds?: string[];
    }) =>
      client
        .post(`customer/${customerId}/reopen-for-review`, {
          json: {
            reason,
            ...(suspectedCustomerIds?.length ? { suspectedCustomerIds } : {}),
          },
        })
        .json<CustomerReopenForReviewResponse>(),
    onSuccess: (_, { customerId }) => {
      queryClient.invalidateQueries({
        queryKey: customerQueryKey.getCustomer(customerId),
      });
      queryClient.invalidateQueries({
        queryKey: [...customerQueryKey._customer, 'list'],
      });
      queryClient.invalidateQueries({
        queryKey: customerQueryKey.getStatusHistory(customerId),
      });
    },
  });

  return {
    reopenForReview: mutation.mutateAsync,
    isPending: mutation.isPending,
  };
};
